import { useState, useCallback, useEffect, useRef } from "react";
import { useWallet } from "@/contexts/WalletContext";
import {
  HOOPS_CONTRACTS,
  ACCOUNT_WASM_HASH,
  TESTNET_TOKENS,
  POOL_ADDRESSES,
  TOKEN_UNIT,
  TOKEN_DECIMALS,
  ADAPTER_IDS,
} from "@/lib/hoops-contracts";
import {
  Contract,
  rpc,
  TransactionBuilder,
  Networks,
  BASE_FEE,
  Address,
  nativeToScVal,
  xdr,
} from "@stellar/stellar-sdk";
import type {
  StepId,
  StepStatus,
  StepData,
  AccountBalances,
  LogEntry,
  SwapQuote,
} from "./types";
import { STEP_ORDER } from "./types";

const SERVER_URL = "https://soroban-testnet.stellar.org";

// Build an LpPlan ScVal struct (alphabetically sorted keys)
function buildLpPlanScVal(
  tokenA: string,
  tokenB: string,
  amountA: bigint,
  amountB: bigint,
  adapterId: bigint
): xdr.ScVal {
  return xdr.ScVal.scvMap([
    new xdr.ScMapEntry({
      key: xdr.ScVal.scvSymbol("adapter_id"),
      val: nativeToScVal(adapterId, { type: "i128" }),
    }),
    new xdr.ScMapEntry({
      key: xdr.ScVal.scvSymbol("amount_a"),
      val: nativeToScVal(amountA, { type: "i128" }),
    }),
    new xdr.ScMapEntry({
      key: xdr.ScVal.scvSymbol("amount_b"),
      val: nativeToScVal(amountB, { type: "i128" }),
    }),
    new xdr.ScMapEntry({
      key: xdr.ScVal.scvSymbol("token_a"),
      val: new Address(tokenA).toScVal(),
    }),
    new xdr.ScMapEntry({
      key: xdr.ScVal.scvSymbol("token_b"),
      val: new Address(tokenB).toScVal(),
    }),
  ]);
}

// Extract detailed error info from simulation or transaction failure
function extractContractError(err: unknown): string {
  if (err && typeof err === "object") {
    // Simulation failure: rpc.Api.SimulateTransactionErrorResponse
    const simErr = err as Record<string, unknown>;
    if (simErr.error && typeof simErr.error === "string") {
      return simErr.error;
    }
    // Check for diagnostic events or result XDR
    if (simErr.events && Array.isArray(simErr.events) && simErr.events.length > 0) {
      return `Contract error with ${String(simErr.events.length)} diagnostic event(s): ${String(simErr.events[0])}`;
    }
  }
  if (err instanceof Error) {
    return err.message;
  }
  return String(err);
}

// Submit tx and poll for result
async function submitAndPoll(
  server: rpc.Server,
  signedTx: ReturnType<typeof TransactionBuilder.fromXDR>
): Promise<rpc.Api.GetSuccessfulTransactionResponse | null> {
  const response = await server.sendTransaction(signedTx);
  if (response.status !== "PENDING") {
    const detail = "errorResult" in response
      ? ` — ${JSON.stringify((response as unknown as Record<string, unknown>).errorResult)}`
      : "";
    throw new Error(`Transaction submission failed: ${response.status}${detail}`);
  }

  let getResponse = await server.getTransaction(response.hash);
  let attempts = 0;
  while ((getResponse.status as string) === "NOT_FOUND" && attempts < 30) {
    await new Promise((resolve) => setTimeout(resolve, 1000));
    getResponse = await server.getTransaction(response.hash);
    attempts++;
  }

  if ((getResponse.status as string) === "SUCCESS") {
    return getResponse as rpc.Api.GetSuccessfulTransactionResponse;
  } else if ((getResponse.status as string) === "FAILED") {
    const failedResp = getResponse as unknown as Record<string, unknown>;
    const resultXdr = failedResp.resultXdr ? ` (resultXdr: ${JSON.stringify(failedResp.resultXdr)})` : "";
    throw new Error(
      `Transaction failed on-chain [hash: ${response.hash}].${resultXdr} Check Stellar Expert for details.`
    );
  }
  return null;
}

// Convert a floating-point token amount to stroops (bigint).
// Uses Math.round to avoid IEEE 754 errors (e.g. 0.1 * 1e7 = 999999.99... with floor).
function toStroops(amount: number): bigint {
  return BigInt(Math.round(amount * TOKEN_UNIT));
}

function formatBalance(stroops: bigint): string {
  const whole = stroops / BigInt(TOKEN_UNIT);
  const frac = stroops % BigInt(TOKEN_UNIT);
  const fracStr = frac.toString().padStart(TOKEN_DECIMALS, "0").slice(0, 2);
  return `${whole.toString()}.${fracStr}`;
}

export function useDemoState() {
  const { isConnected, address, signTransaction } = useWallet();

  // Step management
  const [stepStatuses, setStepStatuses] = useState<Record<StepId, StepStatus>>({
    connect: "active",
    deploy: "locked",
    fund: "locked",
    swap: "locked",
    deposit: "locked",
    withdraw: "locked",
    claim: "locked",
  });
  const [stepData, setStepData] = useState<StepData>({});
  const [processingStep, setProcessingStep] = useState<StepId | null>(null);

  // Account
  const [deployedAccountId, setDeployedAccountId] = useState("");
  const [accountInput, setAccountInput] = useState("");
  const [xlmAmount, setXlmAmount] = useState("10");
  const activeAccountId = deployedAccountId || accountInput;

  // Balances
  const [balances, setBalances] = useState<AccountBalances>({
    xlm: "0.00",
    usdc: "0.00",
    lpSoroswap: "0.00",
    lpAqua: "0.00",
    lastUpdated: null,
    isLoading: false,
  });

  // Swap input + quote
  const [swapXlmInput, setSwapXlmInput] = useState("");
  const [swapQuote, setSwapQuote] = useState<SwapQuote>({ estimatedUsdc: null, loading: false });

  // Logs
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const logIdRef = useRef(0);

  // Server instance
  const serverRef = useRef(new rpc.Server(SERVER_URL));

  // Busy guard to prevent double-click duplicate submissions
  const busyRef = useRef(false);

  // Wallet change detection
  const prevAddressRef = useRef<string | null>(null);
  const initializedRef = useRef(false);

  const defaultStepStatuses: Record<StepId, StepStatus> = {
    connect: "active",
    deploy: "locked",
    fund: "locked",
    swap: "locked",
    deposit: "locked",
    withdraw: "locked",
    claim: "locked",
  };

  const resetBalances: AccountBalances = {
    xlm: "0.00",
    usdc: "0.00",
    lpSoroswap: "0.00",
    lpAqua: "0.00",
    lastUpdated: null,
    isLoading: false,
  };

  // Persist account to scoped sessionStorage
  useEffect(() => {
    if (deployedAccountId && address) {
      sessionStorage.setItem(`hoops_demo_account_${address}`, deployedAccountId);
    }
  }, [deployedAccountId, address]);

  // Unified wallet lifecycle effect
  useEffect(() => {
    // Disconnect
    if (!isConnected || !address) {
      if (initializedRef.current) {
        setDeployedAccountId("");
        setAccountInput("");
        setStepData({});
        setBalances(resetBalances);
        setLogs([]);
        setSwapQuote({ estimatedUsdc: null, loading: false });
        setSwapXlmInput("");
      }
      setStepStatuses(defaultStepStatuses);
      prevAddressRef.current = null;
      initializedRef.current = true;
      return;
    }

    // Wallet changed — full state reset
    if (prevAddressRef.current && prevAddressRef.current !== address) {
      setDeployedAccountId("");
      setAccountInput("");
      setStepData({});
      setBalances(resetBalances);
      setLogs([]);
      setSwapQuote({ estimatedUsdc: null, loading: false });
      setSwapXlmInput("");
    }

    // Connected — restore from scoped sessionStorage
    const saved = sessionStorage.getItem(`hoops_demo_account_${address}`);
    if (saved) {
      setDeployedAccountId(saved);
      setStepStatuses({
        connect: "completed",
        deploy: "completed",
        fund: "active",
        swap: "locked",
        deposit: "locked",
        withdraw: "locked",
        claim: "locked",
      });
    } else {
      setStepStatuses({
        connect: "completed",
        deploy: "active",
        fund: "locked",
        swap: "locked",
        deposit: "locked",
        withdraw: "locked",
        claim: "locked",
      });
    }

    prevAddressRef.current = address;
    initializedRef.current = true;
  }, [isConnected, address]);

  // --- Logging ---
  const addLog = useCallback(
    (level: LogEntry["level"], message: string, txHash?: string, details?: string) => {
      logIdRef.current += 1;
      setLogs((prev) => [
        ...prev,
        {
          id: crypto.randomUUID(),
          timestamp: new Date(),
          level,
          message,
          txHash,
          details,
        },
      ]);
    },
    []
  );

  // --- Step management ---
  const completeStep = useCallback((stepId: StepId) => {
    setStepStatuses((prev) => {
      const next = { ...prev };
      next[stepId] = "completed";
      const idx = STEP_ORDER.indexOf(stepId);
      if (idx < STEP_ORDER.length - 1) {
        const nextStep = STEP_ORDER[idx + 1];
        if (next[nextStep] === "locked") {
          next[nextStep] = "active";
        }
      }
      return next;
    });
    setProcessingStep(null);
  }, []);

  const setStepProcessing = useCallback((stepId: StepId) => {
    setProcessingStep(stepId);
    setStepStatuses((prev) => ({ ...prev, [stepId]: "in-progress" }));
  }, []);

  const setStepActive = useCallback((stepId: StepId) => {
    setProcessingStep(null);
    setStepStatuses((prev) => ({ ...prev, [stepId]: "active" }));
  }, []);

  const goToStep = useCallback((targetStepId: StepId) => {
    if (targetStepId === "connect") return;
    const targetIdx = STEP_ORDER.indexOf(targetStepId);
    setStepStatuses(() => {
      const next = {} as Record<StepId, StepStatus>;
      STEP_ORDER.forEach((id, idx) => {
        if (idx < targetIdx) next[id] = "completed";
        else if (idx === targetIdx) next[id] = "active";
        else next[id] = "locked";
      });
      return next;
    });
    setProcessingStep(null);
  }, []);

  // --- Transaction helper ---
  const signAndSubmit = useCallback(
    async (tx: ReturnType<typeof TransactionBuilder.prototype.build>) => {
      const server = serverRef.current;
      let preparedTx;
      try {
        preparedTx = await server.prepareTransaction(tx);
      } catch (simErr) {
        throw new Error(`Simulation failed: ${extractContractError(simErr)}`);
      }
      if (!signTransaction) throw new Error("Wallet not connected");
      const signedXdr = await signTransaction(preparedTx.toXDR(), Networks.TESTNET);
      const signedTx = TransactionBuilder.fromXDR(signedXdr, Networks.TESTNET);
      return submitAndPoll(server, signedTx);
    },
    [signTransaction]
  );

  // --- Balance querying ---
  const refreshBalances = useCallback(async () => {
    if (!activeAccountId || !address) return;
    setBalances((prev) => ({ ...prev, isLoading: true }));

    try {
      const server = serverRef.current;
      // Use the wallet address (a real Stellar account) as the simulation source.
      // Contract addresses don't have Stellar accounts, so getAccount would fail.
      const sourceAccount = await server.getAccount(address).catch(() => null);
      if (!sourceAccount) {
        setBalances((prev) => ({ ...prev, isLoading: false }));
        return;
      }

      const queryBalance = async (tokenId: string): Promise<bigint> => {
        try {
          const contract = new Contract(tokenId);
          const tx = new TransactionBuilder(sourceAccount, {
            fee: BASE_FEE,
            networkPassphrase: Networks.TESTNET,
          })
            .addOperation(contract.call("balance", new Address(activeAccountId).toScVal()))
            .setTimeout(30)
            .build();

          const sim = await server.simulateTransaction(tx);
          if ("result" in sim && sim.result) {
            const retVal = (sim.result as { retval?: xdr.ScVal }).retval;
            if (retVal) {
              return scValToI128(retVal);
            }
          }
        } catch {
          // Token might not exist for this account
        }
        return 0n;
      };

      // Query Aqua LP shares from adapter's warehouse accounting
      // (LP tokens stay in the adapter due to reward checkpoint costs)
      const queryAquaLpShares = async (): Promise<bigint> => {
        try {
          const adapterContract = new Contract(HOOPS_CONTRACTS.ADAPTERS.AQUA);
          const tx = new TransactionBuilder(sourceAccount, {
            fee: BASE_FEE,
            networkPassphrase: Networks.TESTNET,
          })
            .addOperation(
              adapterContract.call(
                "get_lp_balance",
                new Address(activeAccountId).toScVal(),
                new Address(POOL_ADDRESSES.AQUA_POOL).toScVal()
              )
            )
            .setTimeout(30)
            .build();
          const sim = await server.simulateTransaction(tx);
          if ("result" in sim && sim.result) {
            const retVal = (sim.result as { retval?: xdr.ScVal }).retval;
            if (retVal) return scValToI128(retVal);
          }
        } catch {
          // Adapter might not have been deployed yet
        }
        return 0n;
      };

      const [xlmBal, usdcBal, lpSoroswapBal, lpAquaBal] = await Promise.all([
        queryBalance(TESTNET_TOKENS.XLM),
        queryBalance(TESTNET_TOKENS.USDC),
        queryBalance(POOL_ADDRESSES.SOROSWAP_PAIR),
        queryAquaLpShares(),
      ]);

      setBalances({
        xlm: formatBalance(xlmBal),
        usdc: formatBalance(usdcBal),
        lpSoroswap: formatBalance(lpSoroswapBal),
        lpAqua: formatBalance(lpAquaBal),
        lastUpdated: new Date(),
        isLoading: false,
      });
    } catch {
      setBalances((prev) => ({ ...prev, isLoading: false }));
    }
  }, [activeAccountId, address]);

  // Poll balances when account exists
  useEffect(() => {
    if (!activeAccountId) return;
    void refreshBalances();
    const interval = setInterval(() => { void refreshBalances(); }, 10000);
    return () => { clearInterval(interval); };
  }, [activeAccountId, refreshBalances]);

  // --- Debounced swap quote ---
  useEffect(() => {
    const effectiveXlm = parseFloat(swapXlmInput) || (parseFloat(xlmAmount) || 10) / 2;
    if (!activeAccountId || !address || effectiveXlm <= 0) {
      setSwapQuote({ estimatedUsdc: null, loading: false });
      return;
    }

    setSwapQuote((prev) => ({ ...prev, loading: true }));
    const timer = setTimeout(() => {
      void (async () => {
        try {
          const server = serverRef.current;
          const src = await server.getAccount(address);
          const pairContract = new Contract(POOL_ADDRESSES.SOROSWAP_PAIR);
          const resTx = new TransactionBuilder(src, {
            fee: BASE_FEE,
            networkPassphrase: Networks.TESTNET,
          })
            .addOperation(pairContract.call("get_reserves"))
            .setTimeout(30)
            .build();
          const resSim = await server.simulateTransaction(resTx);
          if ("result" in resSim && resSim.result) {
            const rv = (resSim.result as { retval?: xdr.ScVal }).retval;
            if (rv) {
              const vec = rv.vec();
              if (vec && vec.length >= 2) {
                const reserveUsdc = scValToI128(vec[0]);
                const reserveXlm = scValToI128(vec[1]);
                const xlmStroops = toStroops(effectiveXlm);
                const feeAdj = xlmStroops * 997n;
                const expectedOut = (feeAdj * reserveUsdc) / (reserveXlm * 1000n + feeAdj);
                setSwapQuote({ estimatedUsdc: formatBalance(expectedOut), loading: false });
                return;
              }
            }
          }
          setSwapQuote({ estimatedUsdc: null, loading: false });
        } catch {
          setSwapQuote({ estimatedUsdc: null, loading: false });
        }
      })();
    }, 300);

    return () => { clearTimeout(timer); };
  }, [swapXlmInput, xlmAmount, activeAccountId, address]);

  // --- Actions ---

  const deployAccount = useCallback(async () => {
    if (!address || !signTransaction) return;
    if (busyRef.current) return;
    busyRef.current = true;
    setStepProcessing("deploy");
    addLog("info", "Deploying new smart account...");

    try {
      const server = serverRef.current;
      const sourceAccount = await server.getAccount(address);
      const deployerContract = new Contract(HOOPS_CONTRACTS.ACCOUNT_DEPLOYER);
      const salt = Buffer.from(crypto.getRandomValues(new Uint8Array(32)));

      const deployTx = new TransactionBuilder(sourceAccount, {
        fee: BASE_FEE,
        networkPassphrase: Networks.TESTNET,
      })
        .addOperation(
          deployerContract.call(
            "deploy_account",
            nativeToScVal(address, { type: "address" }),
            nativeToScVal(HOOPS_CONTRACTS.ROUTER, { type: "address" }),
            nativeToScVal(Buffer.from(ACCOUNT_WASM_HASH, "hex"), { type: "bytes" }),
            nativeToScVal(salt, { type: "bytes" })
          )
        )
        .setTimeout(180)
        .build();

      const result = await signAndSubmit(deployTx);

      if (result?.returnValue) {
        const accountId = Address.fromScVal(result.returnValue).toString();
        addLog("success", `Account deployed: ${accountId.slice(0, 12)}...`, undefined, accountId);

        // Initialize
        addLog("info", "Initializing account with router...");
        const freshAccount = await server.getAccount(address);
        const accountContract = new Contract(accountId);

        const initTx = new TransactionBuilder(freshAccount, {
          fee: BASE_FEE,
          networkPassphrase: Networks.TESTNET,
        })
          .addOperation(
            accountContract.call(
              "initialize",
              nativeToScVal(address, { type: "address" }),
              nativeToScVal(HOOPS_CONTRACTS.ROUTER, { type: "address" })
            )
          )
          .setTimeout(180)
          .build();

        const initResult = await signAndSubmit(initTx);
        setDeployedAccountId(accountId);
        const txHash = initResult?.txHash ?? result.txHash;
        setStepData((prev) => ({
          ...prev,
          accountId,
          deployTxHash: txHash,
        }));
        addLog("success", "Account initialized with router!", txHash);
        completeStep("deploy");
        void refreshBalances();
      }
    } catch (_err) {
      addLog("error", _err instanceof Error ? _err.message : "Failed to deploy account");
      setStepActive("deploy");
    } finally {
      busyRef.current = false;
    }
  }, [address, signTransaction, signAndSubmit, addLog, completeStep, setStepProcessing, setStepActive, refreshBalances]);

  const initializeAccount = useCallback(async () => {
    if (!accountInput || !address || !signTransaction) return;
    if (busyRef.current) return;
    busyRef.current = true;
    setStepProcessing("deploy");
    addLog("info", `Initializing existing account ${accountInput.slice(0, 12)}...`);

    try {
      const server = serverRef.current;
      const sourceAccount = await server.getAccount(address);
      const accountContract = new Contract(accountInput);

      const initTx = new TransactionBuilder(sourceAccount, {
        fee: BASE_FEE,
        networkPassphrase: Networks.TESTNET,
      })
        .addOperation(
          accountContract.call(
            "initialize",
            nativeToScVal(address, { type: "address" }),
            nativeToScVal(HOOPS_CONTRACTS.ROUTER, { type: "address" })
          )
        )
        .setTimeout(180)
        .build();

      const result = await signAndSubmit(initTx);
      const txHash = result?.txHash;
      setStepData((prev) => ({ ...prev, accountId: accountInput, deployTxHash: txHash }));
      addLog("success", "Account initialized!", txHash);
      completeStep("deploy");
      void refreshBalances();
    } catch (_err) {
      addLog("error", _err instanceof Error ? _err.message : "Failed to initialize");
      setStepActive("deploy");
    } finally {
      busyRef.current = false;
    }
  }, [accountInput, address, signTransaction, signAndSubmit, addLog, completeStep, setStepProcessing, setStepActive, refreshBalances]);

  const upgradeAccount = useCallback(async () => {
    if (!activeAccountId || !address || !signTransaction) return;
    if (busyRef.current) return;
    busyRef.current = true;
    setStepProcessing("deploy");
    addLog("info", "Upgrading account WASM...");

    try {
      const server = serverRef.current;
      const sourceAccount = await server.getAccount(address);
      const accountContract = new Contract(activeAccountId);

      const upgradeTx = new TransactionBuilder(sourceAccount, {
        fee: BASE_FEE,
        networkPassphrase: Networks.TESTNET,
      })
        .addOperation(
          accountContract.call(
            "upgrade",
            nativeToScVal(Buffer.from(ACCOUNT_WASM_HASH, "hex"), { type: "bytes" })
          )
        )
        .setTimeout(180)
        .build();

      const result = await signAndSubmit(upgradeTx);
      addLog("success", "Account upgraded to latest WASM!", result?.txHash);
      setStepActive("deploy");
    } catch (_err) {
      addLog("error", _err instanceof Error ? _err.message : "Failed to upgrade");
      setStepActive("deploy");
    } finally {
      busyRef.current = false;
    }
  }, [activeAccountId, address, signTransaction, signAndSubmit, addLog, setStepProcessing, setStepActive]);

  const fundAccountWithXLM = useCallback(async () => {
    if (!activeAccountId || !address || !signTransaction) return;
    if (busyRef.current) return;
    busyRef.current = true;
    setStepProcessing("fund");

    const xlmToSend = parseFloat(xlmAmount) || 10;
    const xlmNeeded = toStroops(xlmToSend);

    addLog("info", `Sending ${String(xlmToSend)} XLM to smart account...`);

    try {
      const server = serverRef.current;
      const sourceAccount = await server.getAccount(address);
      const xlmContract = new Contract(TESTNET_TOKENS.XLM);

      const tx = new TransactionBuilder(sourceAccount, {
        fee: BASE_FEE,
        networkPassphrase: Networks.TESTNET,
      })
        .addOperation(
          xlmContract.call(
            "transfer",
            nativeToScVal(address, { type: "address" }),
            nativeToScVal(activeAccountId, { type: "address" }),
            nativeToScVal(xlmNeeded, { type: "i128" })
          )
        )
        .setTimeout(180)
        .build();

      const result = await signAndSubmit(tx);
      const txHash = result?.txHash;
      setStepData((prev) => ({ ...prev, fundTxHash: txHash, fundedXlm: xlmToSend }));
      addLog("success", `Funded account with ${String(xlmToSend)} XLM!`, txHash);
      completeStep("fund");
      void refreshBalances();
    } catch (_err) {
      addLog("error", `Fund failed: ${extractContractError(_err)}`);
      setStepActive("fund");
    } finally {
      busyRef.current = false;
    }
  }, [activeAccountId, address, signTransaction, xlmAmount, signAndSubmit, addLog, completeStep, setStepProcessing, setStepActive, refreshBalances]);

  const swapXlmToUsdc = useCallback(async () => {
    if (!activeAccountId || !address || !signTransaction) return;
    if (busyRef.current) return;
    busyRef.current = true;
    setStepProcessing("swap");

    const effectiveXlm = parseFloat(swapXlmInput) || (parseFloat(xlmAmount) || 10) / 2;
    const xlmToSwap = toStroops(effectiveXlm);
    const deadline = Math.floor(Date.now() / 1000) + 600;

    try {
      const server = serverRef.current;

      // Query pool reserves for a realistic swap estimate
      // Soroswap pair: token_0 = USDC, token_1 = XLM
      let estimateStr = "";
      try {
        const src = await server.getAccount(address);
        const pairContract = new Contract(POOL_ADDRESSES.SOROSWAP_PAIR);
        const resTx = new TransactionBuilder(src, {
          fee: BASE_FEE, networkPassphrase: Networks.TESTNET,
        })
          .addOperation(pairContract.call("get_reserves"))
          .setTimeout(30).build();
        const resSim = await server.simulateTransaction(resTx);
        if ("result" in resSim && resSim.result) {
          const rv = (resSim.result as { retval?: xdr.ScVal }).retval;
          if (rv) {
            const vec = rv.vec();
            if (vec && vec.length >= 2) {
              const reserveUsdc = scValToI128(vec[0]);
              const reserveXlm = scValToI128(vec[1]);
              // Constant-product output with 0.3% fee
              const feeAdj = xlmToSwap * 997n;
              const expectedOut = (feeAdj * reserveUsdc) / (reserveXlm * 1000n + feeAdj);
              estimateStr = formatBalance(expectedOut);
            }
          }
        }
      } catch { /* use fallback */ }

      addLog("info", `Swapping ${String(effectiveXlm)} XLM to ${estimateStr ? `~${estimateStr}` : ""} USDC via Soroswap...`);

      const sourceAccount = await server.getAccount(address);
      const accountContract = new Contract(activeAccountId);

      const tx = new TransactionBuilder(sourceAccount, {
        fee: BASE_FEE,
        networkPassphrase: Networks.TESTNET,
      })
        .addOperation(
          accountContract.call(
            "swap",
            new Address(TESTNET_TOKENS.XLM).toScVal(),
            new Address(TESTNET_TOKENS.USDC).toScVal(),
            nativeToScVal(xlmToSwap, { type: "i128" }),
            new Address(POOL_ADDRESSES.SOROSWAP_PAIR).toScVal(),
            nativeToScVal(deadline, { type: "u32" })
          )
        )
        .setTimeout(180)
        .build();

      const result = await signAndSubmit(tx);
      const txHash = result?.txHash;

      // Refresh balances to get actual post-swap USDC amount
      await refreshBalances();
      addLog("success", `Swapped ${String(effectiveXlm)} XLM to USDC!`, txHash);
      completeStep("swap");
    } catch (_err) {
      addLog("error", `Swap failed: ${extractContractError(_err)}`);
      setStepActive("swap");
    } finally {
      busyRef.current = false;
    }
  }, [activeAccountId, address, signTransaction, swapXlmInput, xlmAmount, signAndSubmit, addLog, completeStep, setStepProcessing, setStepActive, refreshBalances]);

  const deposit = useCallback(async () => {
    if (!activeAccountId || !address || !signTransaction) return;
    if (busyRef.current) return;
    busyRef.current = true;
    setStepProcessing("deposit");

    const deadline = Math.floor(Date.now() / 1000) + 600;

    try {
      const server = serverRef.current;

      // Query actual on-chain balances — no hardcoded rate assumptions
      const walletAccount = await server.getAccount(address);
      const queryBal = async (tokenId: string): Promise<bigint> => {
        try {
          const c = new Contract(tokenId);
          const t = new TransactionBuilder(walletAccount, {
            fee: BASE_FEE,
            networkPassphrase: Networks.TESTNET,
          })
            .addOperation(c.call("balance", new Address(activeAccountId).toScVal()))
            .setTimeout(30)
            .build();
          const sim = await server.simulateTransaction(t);
          if ("result" in sim && sim.result) {
            const retVal = (sim.result as { retval?: xdr.ScVal }).retval;
            if (retVal) return scValToI128(retVal);
          }
        } catch { /* ignore */ }
        return 0n;
      };

      const [usdcBal, xlmBal] = await Promise.all([
        queryBal(TESTNET_TOKENS.USDC),
        queryBal(TESTNET_TOKENS.XLM),
      ]);

      // Minimum thresholds
      const MIN_USDC = toStroops(0.5);
      const XLM_RESERVE = toStroops(10); // Keep 10 XLM for gas/storage fees

      if (usdcBal < MIN_USDC) {
        addLog(
          "error",
          `You have ${formatBalance(usdcBal)} USDC in your Smart Account. ` +
          `Need at least ${formatBalance(MIN_USDC)} USDC to deposit. ` +
          `Swap more XLM or try a smaller amount.`
        );
        setStepActive("deposit");
        busyRef.current = false;
        return;
      }

      const availableXlm = xlmBal > XLM_RESERVE ? xlmBal - XLM_RESERVE : 0n;
      if (availableXlm <= 0n) {
        addLog(
          "error",
          `You have ${formatBalance(xlmBal)} XLM but need to reserve ~10 XLM for fees. ` +
          `Fund the account with more XLM first.`
        );
        setStepActive("deposit");
        busyRef.current = false;
        return;
      }

      // Use actual balances: deposit all available USDC + proportional XLM
      const totalUsdc = usdcBal;
      const halfUsdc = totalUsdc / 2n;
      const xlmForLp = availableXlm;
      const halfXlmLp = xlmForLp / 2n;

      addLog("info", `Depositing ${formatBalance(totalUsdc)} USDC + ${formatBalance(xlmForLp)} XLM into LP (50/50 split)...`);
      addLog("info", `Balance check passed: ${formatBalance(usdcBal)} USDC, ${formatBalance(xlmBal)} XLM available`);

      const accountContract = new Contract(activeAccountId);

      // Split into 2 separate transactions to stay within Soroban resource
      // limits. The Aqua pool's reward checkpointing (~60 persistent storage
      // reads, ~30 writes, 4 external calls) makes a combined 2-pool deposit
      // exceed the per-tx CPU/memory budget.
      const plans = [
        { name: "Aquarius", adapterId: BigInt(ADAPTER_IDS.AQUA), usdc: halfUsdc, xlm: halfXlmLp },
        { name: "Soroswap", adapterId: BigInt(ADAPTER_IDS.SOROSWAP), usdc: totalUsdc - halfUsdc, xlm: xlmForLp - halfXlmLp },
      ];

      let lastTxHash: string | undefined;
      for (const plan of plans) {
        const sourceAccount = await server.getAccount(address);
        const lpPlans = xdr.ScVal.scvVec([
          buildLpPlanScVal(TESTNET_TOKENS.USDC, TESTNET_TOKENS.XLM, plan.usdc, plan.xlm, plan.adapterId),
        ]);

        const tx = new TransactionBuilder(sourceAccount, {
          fee: BASE_FEE,
          networkPassphrase: Networks.TESTNET,
        })
          .addOperation(
            accountContract.call(
              "deposit",
              new Address(TESTNET_TOKENS.USDC).toScVal(),
              nativeToScVal(plan.usdc, { type: "i128" }),
              lpPlans,
              nativeToScVal(deadline, { type: "u32" })
            )
          )
          .setTimeout(180)
          .build();

        const result = await signAndSubmit(tx);
        lastTxHash = result?.txHash;
        addLog("success", `Deposited into ${plan.name} LP!`, lastTxHash);
      }

      setStepData((prev) => ({ ...prev, depositTxHash: lastTxHash }));
      addLog("success", `Deposited ${formatBalance(totalUsdc)} USDC across Aquarius and Soroswap!`);
      completeStep("deposit");
      void refreshBalances();
    } catch (_err) {
      const detail = extractContractError(_err);
      addLog("error", `Deposit failed: ${detail}`);
      setStepActive("deposit");
    } finally {
      busyRef.current = false;
    }
  }, [activeAccountId, address, signTransaction, signAndSubmit, addLog, completeStep, setStepProcessing, setStepActive, refreshBalances]);

  // --- Withdraw action ---
  const withdraw = useCallback(async () => {
    if (!activeAccountId || !address || !signTransaction) return;
    if (busyRef.current) return;
    busyRef.current = true;
    setStepProcessing("withdraw");
    addLog("info", "Withdrawing LP positions...");

    try {
      const server = serverRef.current;
      const accountContract = new Contract(activeAccountId);
      const deadline = Math.floor(Date.now() / 1000) + 300;

      // Auto-upgrade account contract to latest WASM (picks up new redeem/claim signatures)
      addLog("info", "Upgrading account contract to latest version...");
      const upgradeSource = await server.getAccount(address);
      const upgradeTx = new TransactionBuilder(upgradeSource, {
        fee: BASE_FEE,
        networkPassphrase: Networks.TESTNET,
      })
        .addOperation(
          accountContract.call(
            "upgrade",
            nativeToScVal(Buffer.from(ACCOUNT_WASM_HASH, "hex"), { type: "bytes" })
          )
        )
        .setTimeout(60)
        .build();
      await signAndSubmit(upgradeTx);
      addLog("success", "Account upgraded!");

      // Withdraw from Aqua LP (adapter warehouse pattern)
      // Query current Aqua LP balance from adapter
      const aquaShares = await (async () => {
        try {
          const sa = await server.getAccount(address);
          const adapterContract = new Contract(HOOPS_CONTRACTS.ADAPTERS.AQUA);
          const tx = new TransactionBuilder(sa, {
            fee: BASE_FEE,
            networkPassphrase: Networks.TESTNET,
          })
            .addOperation(
              adapterContract.call(
                "get_lp_balance",
                new Address(activeAccountId).toScVal(),
                new Address(POOL_ADDRESSES.AQUA_POOL).toScVal()
              )
            )
            .setTimeout(30)
            .build();
          const sim = await server.simulateTransaction(tx);
          if ("result" in sim && sim.result) {
            const retVal = (sim.result as { retval?: xdr.ScVal }).retval;
            if (retVal) return scValToI128(retVal);
          }
        } catch { /* no shares */ }
        return 0n;
      })();

      // Query Soroswap LP balance (standard token balance on the Account)
      const soroswapShares = await (async () => {
        try {
          const sa = await server.getAccount(address);
          const lpContract = new Contract(POOL_ADDRESSES.SOROSWAP_PAIR);
          const tx = new TransactionBuilder(sa, {
            fee: BASE_FEE,
            networkPassphrase: Networks.TESTNET,
          })
            .addOperation(lpContract.call("balance", new Address(activeAccountId).toScVal()))
            .setTimeout(30)
            .build();
          const sim = await server.simulateTransaction(tx);
          if ("result" in sim && sim.result) {
            const retVal = (sim.result as { retval?: xdr.ScVal }).retval;
            if (retVal) return scValToI128(retVal);
          }
        } catch { /* no shares */ }
        return 0n;
      })();

      addLog("info", `LP positions: Aqua=${formatBalance(aquaShares)}, Soroswap=${formatBalance(soroswapShares)}`);

      let lastTxHash: string | undefined;

      // Withdraw via V2 batch_redeem — combine all LP positions into one TX
      const redeemPlans: xdr.ScVal[] = [];

      if (aquaShares > 0n) {
        addLog("info", "Preparing Aquarius withdrawal...");
        const sourceAccount = await server.getAccount(address);
        // Get the Aqua pool's share token address for the lp_token param
        const aquaPoolContract = new Contract(POOL_ADDRESSES.AQUA_POOL);
        const shareIdTx = new TransactionBuilder(sourceAccount, {
          fee: BASE_FEE,
          networkPassphrase: Networks.TESTNET,
        })
          .addOperation(aquaPoolContract.call("share_id"))
          .setTimeout(30)
          .build();
        const shareIdSim = await server.simulateTransaction(shareIdTx);
        let aquaLpToken = POOL_ADDRESSES.AQUA_POOL; // fallback
        if ("result" in shareIdSim && shareIdSim.result) {
          const retVal = (shareIdSim.result as { retval?: xdr.ScVal }).retval;
          if (retVal) {
            aquaLpToken = Address.fromScVal(retVal).toString();
          }
        }

        redeemPlans.push(xdr.ScVal.scvMap([
          new xdr.ScMapEntry({ key: xdr.ScVal.scvSymbol("adapter_id"), val: nativeToScVal(ADAPTER_IDS.AQUA, { type: "i128" }) }),
          new xdr.ScMapEntry({ key: xdr.ScVal.scvSymbol("lp_amount"), val: nativeToScVal(aquaShares, { type: "i128" }) }),
          new xdr.ScMapEntry({ key: xdr.ScVal.scvSymbol("lp_token"), val: new Address(aquaLpToken).toScVal() }),
          new xdr.ScMapEntry({ key: xdr.ScVal.scvSymbol("pool_address"), val: new Address(POOL_ADDRESSES.AQUA_POOL).toScVal() }),
        ]));
      }

      if (soroswapShares > 0n) {
        addLog("info", "Preparing Soroswap withdrawal...");
        redeemPlans.push(xdr.ScVal.scvMap([
          new xdr.ScMapEntry({ key: xdr.ScVal.scvSymbol("adapter_id"), val: nativeToScVal(ADAPTER_IDS.SOROSWAP, { type: "i128" }) }),
          new xdr.ScMapEntry({ key: xdr.ScVal.scvSymbol("lp_amount"), val: nativeToScVal(soroswapShares, { type: "i128" }) }),
          new xdr.ScMapEntry({ key: xdr.ScVal.scvSymbol("lp_token"), val: new Address(POOL_ADDRESSES.SOROSWAP_PAIR).toScVal() }),
          new xdr.ScMapEntry({ key: xdr.ScVal.scvSymbol("pool_address"), val: new Address(POOL_ADDRESSES.SOROSWAP_PAIR).toScVal() }),
        ]));
      }

      if (redeemPlans.length > 0) {
        addLog("info", "Withdrawing via batch_redeem...");
        const freshSource = await server.getAccount(address);
        const redeemTx = new TransactionBuilder(freshSource, {
          fee: BASE_FEE,
          networkPassphrase: Networks.TESTNET,
        })
          .addOperation(
            accountContract.call(
              "batch_redeem",
              xdr.ScVal.scvVec(redeemPlans),
              nativeToScVal(deadline, { type: "u32" })
            )
          )
          .setTimeout(180)
          .build();

        const result = await signAndSubmit(redeemTx);
        lastTxHash = result?.txHash;
        addLog("success", `Withdrew from ${String(redeemPlans.length)} pool(s)!`, lastTxHash);
      }

      if (aquaShares === 0n && soroswapShares === 0n) {
        addLog("info", "No LP positions to withdraw.");
      }

      setStepData((prev) => ({ ...prev, withdrawTxHash: lastTxHash }));
      addLog("success", "Withdrawal complete! Tokens returned to your wallet.");
      completeStep("withdraw");
      void refreshBalances();
    } catch (_err) {
      const detail = extractContractError(_err);
      addLog("error", `Withdraw failed: ${detail}`);
      setStepActive("withdraw");
    } finally {
      busyRef.current = false;
    }
  }, [activeAccountId, address, signTransaction, signAndSubmit, addLog, completeStep, setStepProcessing, setStepActive, refreshBalances]);

  // --- Claim rewards action ---
  const claimRewards = useCallback(async () => {
    if (!activeAccountId || !address || !signTransaction) return;
    if (busyRef.current) return;
    busyRef.current = true;
    setStepProcessing("claim");
    addLog("info", "Claiming Aqua pool rewards...");

    try {
      const server = serverRef.current;
      const accountContract = new Contract(activeAccountId);

      // Check if there are any Aqua LP shares (rewards only accrue if we deposited)
      const aquaShares = await (async () => {
        try {
          const sa = await server.getAccount(address);
          const adapterContract = new Contract(HOOPS_CONTRACTS.ADAPTERS.AQUA);
          const tx = new TransactionBuilder(sa, {
            fee: BASE_FEE,
            networkPassphrase: Networks.TESTNET,
          })
            .addOperation(
              adapterContract.call(
                "get_lp_balance",
                new Address(activeAccountId).toScVal(),
                new Address(POOL_ADDRESSES.AQUA_POOL).toScVal()
              )
            )
            .setTimeout(30)
            .build();
          const sim = await server.simulateTransaction(tx);
          if ("result" in sim && sim.result) {
            const retVal = (sim.result as { retval?: xdr.ScVal }).retval;
            if (retVal) return scValToI128(retVal);
          }
        } catch { /* no shares */ }
        return 0n;
      })();

      if (aquaShares === 0n) {
        addLog("info", "No Aqua LP position found — nothing to claim.");
        completeStep("claim");
        return;
      }

      // Call account.claim(adapter, pool, reward_token)
      // The reward token for Aqua testnet pools is typically AQUA or the pool's configured reward
      // For now, we pass USDC as a placeholder — the actual reward token depends on pool config
      const sourceAccount = await server.getAccount(address);
      const claimTx = new TransactionBuilder(sourceAccount, {
        fee: BASE_FEE,
        networkPassphrase: Networks.TESTNET,
      })
        .addOperation(
          accountContract.call(
            "claim",
            new Address(HOOPS_CONTRACTS.ADAPTERS.AQUA).toScVal(),
            new Address(POOL_ADDRESSES.AQUA_POOL).toScVal(),
            new Address(TESTNET_TOKENS.USDC).toScVal() // reward token — adjust if pool uses different token
          )
        )
        .setTimeout(180)
        .build();

      const result = await signAndSubmit(claimTx);
      const txHash = result?.txHash;
      setStepData((prev) => ({ ...prev, claimTxHash: txHash }));
      addLog("success", "Rewards claimed!", txHash);
      completeStep("claim");
      void refreshBalances();
    } catch (_err) {
      const detail = extractContractError(_err);
      // Rewards may not have accrued yet — treat as informational
      if (detail.includes("PoolClaimKilled") || detail.includes("claim")) {
        addLog("info", "No rewards accrued yet (pool rewards may not be active on testnet).");
        completeStep("claim");
      } else {
        addLog("error", `Claim failed: ${detail}`);
        setStepActive("claim");
      }
    } finally {
      busyRef.current = false;
    }
  }, [activeAccountId, address, signTransaction, signAndSubmit, addLog, completeStep, setStepProcessing, setStepActive, refreshBalances]);

  return {
    // Wallet
    isConnected,
    address,
    // Steps
    stepStatuses,
    stepData,
    processingStep,
    goToStep,
    // Account
    activeAccountId,
    accountInput,
    setAccountInput,
    xlmAmount,
    setXlmAmount,
    // Swap
    swapXlmInput,
    setSwapXlmInput,
    swapQuote,
    // Balances
    balances,
    refreshBalances,
    // Logs
    logs,
    // Actions
    deployAccount,
    initializeAccount,
    upgradeAccount,
    fundAccountWithXLM,
    swapXlmToUsdc,
    deposit,
    withdraw,
    claimRewards,
  };
}

// Parse an ScVal i128 to bigint
function scValToI128(val: xdr.ScVal): bigint {
  try {
    const i128 = val.i128();
    const hi = BigInt(i128.hi().toString());
    const lo = BigInt(i128.lo().toString());
    return (hi << 64n) | lo;
  } catch {
    return 0n;
  }
}
